/**
 * Ein Kurzhinweis am Fragezeichen — `HmHint` aus
 * `ui_kits/dashboard/MesskonzeptScreen.jsx`.
 *
 * **Er trägt, was beim ersten Einrichten richtig ist und danach überflüssig**:
 * was ein Steckplatz bedeutet und welche Größe er liefert. Dauerhaft im Bild
 * wären es zwei Zeilen je Steckplatz, bei einem Mehrfamilienhaus zwanzig.
 *
 * Anders als im Entwurf ist der Text **auch ohne Maus erreichbar**: Er steht
 * als `aria-label` am Knopf und erscheint beim Fokus. Und er öffnet nach
 * unten, wenn nach oben kein Platz ist — `Card` schneidet ab
 * (`overflow: hidden`), deshalb wird vor dem Öffnen gemessen statt gehofft.
 */
import { useRef, useState } from "react";

export function HintMark({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [down, setDown] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  const show = () => {
    const element = ref.current;
    if (element !== null) {
      let top = 0;
      for (let parent = element.parentElement; parent !== null; parent = parent.parentElement) {
        if (getComputedStyle(parent).overflowY !== "visible") {
          top = parent.getBoundingClientRect().top;
          break;
        }
      }
      setDown(element.getBoundingClientRect().top - top < 86);
    }
    setOpen(true);
  };

  return (
    <span
      ref={ref}
      style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={text}
        onFocus={show}
        onBlur={() => setOpen(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 17,
          height: 17,
          padding: 0,
          borderRadius: "50%",
          border: "1px solid var(--border-default)",
          background: "transparent",
          color: "var(--text-muted)",
          cursor: "help",
          fontFamily: "var(--font-body)",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ?
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            [down ? "top" : "bottom"]: "calc(100% + 7px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            width: 224,
            maxWidth: "70vw",
            padding: "8px 10px",
            borderRadius: "var(--radius-sm)",
            background: "var(--surface-tooltip)",
            color: "var(--text-on-tooltip)",
            boxShadow: "var(--shadow-md)",
            fontSize: 12,
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
